import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScrollIntoViewDirective } from '../../directives/scroll-into-view.directive';

@Component({
  selector: 'eng-typeahead',
  imports: [FormsModule, ScrollIntoViewDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadComponent),
      multi: true,
    },
  ],
  templateUrl: './typeahead.component.html',
  styleUrl: './typeahead.component.scss',
})
export class TypeaheadComponent implements ControlValueAccessor, OnInit {
  private _selectedItem?: KeyValuePair | null;
  private _data: KeyValuePair[] | GroupedData<KeyValuePair>[] = [];

  public filteredData: (KeyValuePair | GroupedData<KeyValuePair>)[] = [];
  public showDropdown = false;
  public searchTerm = '';
  public disabled = false;
  public onChange = (value: any) => {};
  public onTouched = () => {};

  public get selectedItem(): KeyValuePair | null | undefined {
    return this._selectedItem;
  }

  @Input()
  public ngbAutoFocus = false;

  @Input()
  public set selectedItem(value: KeyValuePair | null | undefined) {
    this.searchTerm = value?.value;
    this._selectedItem = value;

    if (value === undefined || value === null) {
      this.searchTerm = '';
      this.filterData('');
    }

    this.onTouched();
  }

  public get selectedKey(): any {
    return this._selectedItem?.key;
  }

  @Input()
  public set selectedKey(value: any) {
    this.selectedItem = this.flattenedData().find(x => x.key === value);
  }

  @Output()
  public selectedKeyChange = new EventEmitter<any>();

  public get data(): KeyValuePair[] | GroupedData<KeyValuePair>[] {
    return this._data;
  }

  @Input({ required: true })
  public set data(value: KeyValuePair[] | GroupedData<KeyValuePair>[]) {
    this._data = value;
    this.filterData(this.searchTerm);
  }

  @Input()
  public placeholder: string = 'Search...';

  @Output()
  public selected = new EventEmitter<KeyValuePair>();

  constructor() {}

  writeValue(obj: any): void {
    this.selectedKey = obj;
  }

  public registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit() {
    this.filteredData = this.data ? [...this.data] : [];
  }

  onSearchChange() {
    this.showDropdown = true;
    this._selectedItem = null;

    this.filterData(this.searchTerm);
  }

  filterData(searchTerm: string) {
    const searchLower = searchTerm.toLowerCase();
    this.filteredData =
      Array.isArray(this.data) && this.isGroupedData(this.data)
        ? this.data
            .map(group => ({
              ...group,
              items: group.items.filter(item => item.value.toLowerCase().includes(searchLower)),
            }))
            .filter(group => group.items.length > 0)
        : (this.data as KeyValuePair[]).filter(item => item.value.toLowerCase().includes(searchLower));

    //On exact match select the item.
    const flat = this.flattenedData();
    if (flat.length === 1 && flat[0].value.toLowerCase() === searchLower) {
      this.selectedItem = flat[0];
      this.onSelect(flat[0]);
    }
  }

  isGroupedData(data: any[]): data is GroupedData<KeyValuePair>[] {
    return data.length > 0 && 'groupName' in data[0] && 'items' in data[0];
  }

  isFlatData(data: any[]): data is KeyValuePair[] {
    return data.length > 0 && 'key' in data[0] && 'value' in data[0];
  }

  toggleDropdown() {
    if (this.disabled) {
      this.showDropdown = false;
      return;
    }

    this.showDropdown = !this.showDropdown;
  }

  onSelect(item: KeyValuePair) {
    if (this.disabled) return;

    this.selectedItem = item;
    this.searchTerm = item.value;
    this.onChange(item.key);
    this.selected.emit(item);
    this.selectedKeyChange.emit(item.key);
    this.showDropdown = false;
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.disabled || !this.showDropdown || this.filteredData.length === 0) return;

    const flatList = this.flattenedData();
    let selectedIndex = this.selectedItem ? flatList.findIndex(x => x == this.selectedItem) : -1;

    if (event.key === 'ArrowDown') {
      this.selectedItem = flatList[(selectedIndex + 1) % flatList.length];
    } else if (event.key === 'ArrowUp') {
      this.selectedItem = flatList[(selectedIndex - 1 + flatList.length) % flatList.length];
    } else if (event.key === 'Enter' && selectedIndex > -1) {
      this.onSelect(flatList[selectedIndex]);
    }
  }

  flattenedData(): KeyValuePair[] {
    return this.isGroupedData(this.filteredData)
      ? this.filteredData.flatMap(group => group.items)
      : (this.filteredData as KeyValuePair[]);
  }
}

export interface KeyValuePair {
  key: any;
  value: any;
}

export interface GroupedData<T extends KeyValuePair> {
  groupName: string;
  items: T[];
}
